import { daysFormat } from '@/filters'
import { decimalDiv, decimalMul, decimalSub } from '@/utils/getCalculatedValue'
import {
  getBidCreateDate,
  getDefaultBidOrgUid
} from '@/utils/pageParams/progress'
import {
  removeNull,
  getTreeArray,
  getSameAttribute
} from '@/utils/handleData.js'
const PROGRESS_SUM_KEYS = [
  'imageQuantity',
  'currentDoneQuantity',
  'currentDoneAmount',
  'contractAmount',
  'listDesignAmount'
]
const listDesignByDoneQty = (item) => {
  const qty = Number(item && item.currentDoneQuantity)
  const amt = Number(item && item.currentDoneAmount)
  const image = Number(item && item.imageQuantity)
  if (qty > 0 && image > 0 && !isNaN(amt)) {
    return decimalMul(decimalDiv(amt, qty), image)
  }
  const contract = Number(item && item.contractAmount)
  return isNaN(contract) ? 0 : contract
}
const inferPeriodType = (start, end, totalStart) => {
  const today = daysFormat(new Date())
  const now = new Date()
  const monthStart = daysFormat(new Date(now.getFullYear(), now.getMonth(), 1))
  const yearStart = daysFormat(new Date(now.getFullYear(), 0, 1))
  if (!start || start < '1990-01-01') return 'total'
  if (totalStart && start === totalStart && end === today) return 'total'
  if (start === today && end === today) return 'today'
  if (start === monthStart && end === today) return 'month'
  if (start === yearStart && end === today) return 'year'
  return 'range'
}
const flattenCascaderValues = (value) => {
  if (value == null || value === '') return []
  const list = Array.isArray(value) ? value : [value]
  return list
    .map((item) => {
      if (Array.isArray(item)) return item[item.length - 1]
      return item
    })
    .filter((item) => item !== undefined && item !== null && item !== '')
}
const collectDescendantLeaves = (node) => {
  const uids = []
  const walk = (current) => {
    if (!current) return
    const kids = current.children || []
    if (!kids.length) {
      if (current.uid || current.value) uids.push(current.uid || current.value)
      return
    }
    kids.forEach(walk)
  }
  walk(node)
  return uids
}
const collectLeafUidsByIds = (tree, selected) => {
  const want = {}
  flattenCascaderValues(selected).forEach((id) => {
    want[String(id)] = true
  })
  const out = []
  const walk = (arr) => {
    (arr || []).forEach((node) => {
      if (!node) return
      const id = String(node.uid || node.value || '')
      if (want[id]) {
        collectDescendantLeaves(node).forEach((uid) => out.push(uid))
      }
      if (node.children && node.children.length) walk(node.children)
    })
  }
  walk(tree)
  return Array.from(new Set(out.filter(Boolean)))
}
const flattenReportRows = (rows) => {
  const out = []
  const list = rows || []
  list.forEach((item) => {
    if (item && item.isSubitemGroup) {
      flattenReportRows(item.children).forEach((row) => out.push(row))
    } else if (item) {
      out.push(item)
    }
  })
  return out
}
const remainingOf = (row) => {
  const n = Number(row && (row.rateBase != null ? row.rateBase : row.residueQuantity))
  return isNaN(n) ? 0 : n
}
const applyRemainFields = (item, doneQty) => {
  if (!item) return item
  const done = Number(doneQty)
  item.doneQuantity = isNaN(done) ? 0 : done
  item.residueQuantity = decimalSub(item.designQuantity, item.doneQuantity)
  if (Number(item.residueQuantity) < 0) item.residueQuantity = 0
  item.rateBase = item.residueQuantity
  return item
}
const rateFromRemainQty = (qty, base) => {
  const q = Number(qty)
  const b = Number(base)
  if (!(b > 0) || isNaN(q)) return 0
  const rate = Number(decimalMul(decimalDiv(q, b), 100))
  if (isNaN(rate)) return 0
  return Math.max(0, Math.round(rate))
}
const listCapKey = (row) => {
  return String(
    (row && (row.listBreakdownUid || row.listBreakdownCode || row.uid)) || ''
  )
}
const listSiblingRateMax = (row, dates) => {
  const key = listCapKey(row)
  if (!key) return 100
  const other = (dates || []).reduce((sum, date) => {
    return (
      sum +
      ((date && date.children) || []).reduce((inner, item) => {
        if (
          item &&
          item.rowKind === 'list' &&
          item.uid !== row.uid &&
          listCapKey(item) === key
        ) {
          return inner + Math.round(Number(item.doneRate || 0))
        }
        return inner
      }, 0)
    )
  }, 0)
  return Math.max(0, 100 - other)
}
const refreshCategoryRateCaps = (nodes) => {
  (nodes || []).forEach((node) => {
    if (!node) return
    if (node.rowKind === 'category' || node.isCategory) {
      const dates = (node.children || []).filter(
        (child) => child && child.rowKind === 'date'
      )
      dates.forEach((row) => {
        const other = dates
          .filter((item) => item && item.uid !== row.uid)
          .reduce((sum, item) => sum + Math.round(Number(item.doneRate || 0)), 0)
        row.rateMax = Math.max(0, 100 - other)
      })
      dates.forEach((date) => {
        const children = (date && date.children) || []
        children.forEach((list) => {
          if (!list || list.rowKind !== 'list') return
          list.rateMax = listSiblingRateMax(list, dates)
        })
      })
    }
    if (node.rowKind === 'image') {
      node.rateMax = remainingOf(node) > 0 ? 100 : 0
    }
    if (node.rowKind === 'list') {
      const remainMax = remainingOf(node) > 0 ? 100 : 0
      const cur = node.rateMax != null ? Number(node.rateMax) : 100
      node.rateMax = Math.min(remainMax, isNaN(cur) ? 100 : cur)
    }
    if (node.children && node.children.length) {
      refreshCategoryRateCaps(node.children)
    }
  })
  return nodes
}
const isReportQtyRow = (row) => {
  if (!row || row.isSubitemGroup) return false
  return (
    row.residueQuantity != null ||
    row.designQuantity != null ||
    row.imageQuantity != null ||
    row.listBreakdownUid
  )
}
const pruneCompletedReportRows = (nodes) => {
  const out = []
  const source = nodes || []
  source.forEach((node) => {
    if (!node) return
    if (node.isSubitemGroup) {
      const children = pruneCompletedReportRows(node.children)
      if (children.length) {
        out.push(Object.assign({}, node, { children }))
      }
      return
    }
    if (isReportQtyRow(node) && remainingOf(node) <= 0) return
    const children = pruneCompletedReportRows(node.children)
    out.push(Object.assign({}, node, { children }))
  })
  return out
}
const resetNewReportInputs = (nodes) => {
  (nodes || []).forEach((node) => {
    if (!node) return
    if (!node.isSubitemGroup && isReportQtyRow(node)) {
      node.uploadDate = ''
      node.completedQuantities = null
      node.doneRate = null
      node.remark = node.remark ? node.remark : ''
    }
    if (node.children && node.children.length) {
      resetNewReportInputs(node.children)
    }
  })
  return nodes
}
const toUploadDateText = (value) => {
  if (value === undefined || value === null || value === '') return ''
  const matched = String(value).match(/(\d{4}-\d{2}-\d{2})/)
  return matched ? matched[1] : ''
}
const sumCompletedQty = (list) => {
  return (list || []).reduce((sum, item) => {
    const n = Number(item && item.completedQuantities)
    return sum + (isNaN(n) ? 0 : n)
  }, 0)
}
const compareUploadRecency = (left, right) => {
  const dateLeft = toUploadDateText(left && left.uploadDate)
  const dateRight = toUploadDateText(right && right.uploadDate)
  if (dateLeft !== dateRight) return dateLeft < dateRight ? -1 : 1
  const createLeft = String((left && left.createTime) || '')
  const createRight = String((right && right.createTime) || '')
  if (createLeft !== createRight) return createLeft < createRight ? -1 : 1
  const updateLeft = String((left && left.updateTime) || '')
  const updateRight = String((right && right.updateTime) || '')
  if (updateLeft !== updateRight) return updateLeft < updateRight ? -1 : 1
  return String((left && left.uid) || '').localeCompare(String((right && right.uid) || ''))
}
const keepLastMineUploads = (rows, userUid) => {
  const out = []
  const currentUid = userUid ? String(userUid) : ''
  ;(rows || []).forEach((item) => {
    if (!item) return
    const uploads = (item.progressUploadList || []).filter(Boolean)
    if (!uploads.length) return
    const sorted = uploads.slice().sort(compareUploadRecency)
    const last = sorted[sorted.length - 1]
    if (
      currentUid &&
      last.operateUserUid &&
      String(last.operateUserUid) !== currentUid
    ) {
      return
    }
    const previous = sorted.slice(0, -1)
    const othersDone = Number(item.othersDoneQuantity || 0) + sumCompletedQty(previous)
    const lastId = String(last.uid || '')
    const lists = (item.listHook || []).map((list) => {
      if (!list) return list
      const completions = list.listCompletion || []
      const kept = completions.filter(
        (row) => row && String(row.uploadUid) === lastId
      )
      const dropped = completions.filter(
        (row) => row && String(row.uploadUid) !== lastId
      )
      return Object.assign({}, list, {
        listCompletion: kept,
        othersDoneQuantity:
          Number(list.othersDoneQuantity || 0) + sumCompletedQty(dropped)
      })
    })
    out.push(
      Object.assign({}, item, {
        progressUploadList: [last],
        othersDoneQuantity: othersDone,
        listHook: lists
      })
    )
  })
  return out
}
const collectTreeUids = (nodes) => {
  const uids = []
  const walk = (list) => {
    (list || []).forEach((node) => {
      if (!node) return
      if (node.uid) uids.push(node.uid)
      if (node.children && node.children.length) walk(node.children)
    })
  }
  walk(nodes)
  return uids
}
const codePrefix = (name) => {
  const text = String(name || '').trim()
  if (!text) return ''
  const matched = text.match(/^[^\s\xa0]+/)
  return matched ? matched[0] : text
}
const bizCodeOf = (node) => {
  if (!node) return ''
  if (node.rowKind === 'date') return node.uploadDate || codePrefix(node.codeName)
  if (node.isSubitemGroup || node.rowKind === 'subitem') {
    return node.subitemCode || codePrefix(node.codeName)
  }
  if (
    node.rowKind === 'image' ||
    node.rowKind === 'category' ||
    node.isCategory
  ) {
    return node.progressItemCode || codePrefix(node.codeName)
  }
  if (
    node.rowKind === 'list' ||
    node.listBreakdownUid ||
    (node.listBreakdownCode && !node.progressItemType)
  ) {
    return node.listBreakdownCode || codePrefix(node.codeName)
  }
  if (node.progressItemCode) return node.progressItemCode
  return node.subitemCode || codePrefix(node.codeName)
}
const compareRegionLikeCode = (left, right) => {
  const a = String(left || '')
  const b = String(right || '')
  if (a === b) return 0
  const minLen = Math.min(a.length, b.length)
  const headA = a.slice(0, minLen)
  const headB = b.slice(0, minLen)
  if (headA !== headB) {
    return headA.localeCompare(headB, 'zh-CN', { sensitivity: 'base' })
  }
  return a.length - b.length
}
const sortProgressBizTree = (nodes) => {
  const list = (nodes || []).slice().sort((a, b) => {
    return compareRegionLikeCode(bizCodeOf(a), bizCodeOf(b))
  })
  list.forEach((node) => {
    if (node && node.children && node.children.length) {
      node.children = sortProgressBizTree(node.children)
    }
  })
  return list
}
const pruneEmptyBoundTypeTree = (nodes) => {
  return (nodes || [])
    .map((node) => {
      if (!node) return null
      const next = Object.assign({}, node)
      if (next.children && next.children.length) {
        next.children = pruneEmptyBoundTypeTree(next.children)
      } else {
        next.children = null
      }
      return next
    })
    .filter((node) => {
      if (!node) return false
      if (node.subitemUid) return true
      return !!(node.children && node.children.length)
    })
}
const pruneToProgressTypeLevel = (nodes) => {
  const out = []
  ;(nodes || []).forEach((node) => {
    if (!node || node.subitemUid) return
    const next = Object.assign({}, node)
    const kids = pruneToProgressTypeLevel(node.children || [])
    const hadSubitemLeaf = (node.children || []).some(
      (child) => child && child.subitemUid
    )
    next.children = kids.length ? kids : null
    if (next.children || hadSubitemLeaf) out.push(next)
  })
  return out
}
const decorateBoundProgressTypeItem = (item) => {
  if (!item) return item
  item.value = item.uid
  if (item.subitemUid && item.subitemCode) {
    item.label = (
      (item.subitemCode || '') +
      '\xa0\xa0' +
      (item.subitemName || '')
    ).trim()
    item.rowKind = 'subitem'
  } else {
    item.label = (
      (item.progressItemCode || '') +
      '\xa0\xa0' +
      (item.progressItemType || '')
    ).trim()
  }
  item.codeName = item.label
  return item
}
const decorateProgressItems = (rows) => {
  const subitemProgressInfo = (rows || []).filter((item) => item && item.subitemUid)
  subitemProgressInfo.forEach((item) => {
    item.progressUid = item.uid
    item.rowKind = 'image'
    item.codeName = item.progressItemCode + '\xa0\xa0' + item.progressItemType
    item.designQuantity = item.imageQuantity
    item.children = item.listHook ? item.listHook : []
        item.children.forEach((child) => {
          child.listBreakdownUid = child.uid
          child.rowKind = 'list'
          child.codeName =
            child.listBreakdownCode + '\xa0\xa0' + child.listBreakdownName
          child.designQuantity = child.billCheckQuantity ? child.billCheckQuantity : 0
          child.marker = child.drawingNo ? child.drawingNo : ''
          child.listCompletion = child.listCompletion ? child.listCompletion : []
          child.doneQuantity = getSameAttribute(
            child.listCompletion,
            'completedQuantities'
          ).addValue
          applyRemainFields(child, child.doneQuantity)
        })
        item.children = sortProgressBizTree(item.children)
    item.progressUploadList = item.progressUploadList ? item.progressUploadList : []
    item.doneQuantity = getSameAttribute(
      item.progressUploadList,
      'completedQuantities'
    ).addValue
    applyRemainFields(item, item.doneQuantity)
  })
  return subitemProgressInfo
}
const rollupEditTree = (nodes, options) => {
  const viewMode = !!(options && options.viewMode)
  const source = nodes || []
  source.forEach((node) => {
    if (!node || !node.children || !node.children.length) return
    rollupEditTree(node.children, options)
    if (node.isCategory || node.rowKind === 'category') {
      node.completedQuantities = getSameAttribute(
        node.children,
        'completedQuantities'
      ).addValue
      if (viewMode) {
        applyRemainFields(node, node.completedQuantities)
      }
    }
  })
  refreshCategoryRateCaps(nodes)
  return nodes
}
const buildEditTree = (items, options) => {
  const othersAsZero = !!(options && options.othersAsZero)
  const viewMode = !!(options && options.viewMode)
  const grouped = []
  const subMap = {}
  const source = items || []
  source.forEach((item) => {
    if (!item || !item.subitemUid) return
    const sid = item.subitemUid
    const othersDone = othersAsZero ? 0 : Number(item.othersDoneQuantity || 0)
    const remain = Number(
      decimalSub(item.designQuantity, isNaN(othersDone) ? 0 : othersDone)
    )
    const rateBase = remain < 0 ? 0 : remain
    if (!subMap[sid]) {
      subMap[sid] = {
        uid: 'sub-' + sid,
        subitemUid: sid,
        codeName: ((item.subitemCode || '') + '\xa0\xa0' + (item.subitemName || '')).trim(),
        subitemCode: item.subitemCode,
        subitemName: item.subitemName,
        isSubitemGroup: true,
        rowKind: 'subitem',
        children: []
      }
      grouped.push(subMap[sid])
    }
    const uploads = (item.progressUploadList || []).filter((upload) => {
      return upload && (upload.completedQuantities || upload.completedQuantities === 0)
    })
    const dateRows = uploads.map((upload) => {
      const listRows = []
      const lists = item.children || []
      lists.forEach((list) => {
        const completions = list.listCompletion || []
        completions.forEach((completion) => {
          if (!completion || String(completion.uploadUid) !== String(upload.uid)) {
            return
          }
          const qty = Number(completion.completedQuantities || 0)
          const listOthers = othersAsZero
            ? 0
            : Number(list.othersDoneQuantity || 0)
          const listRemain = Number(
            decimalSub(list.designQuantity, isNaN(listOthers) ? 0 : listOthers)
          )
          const listBase = listRemain < 0 ? 0 : listRemain
          const listRate = Math.min(100, rateFromRemainQty(qty, listBase))
          listRows.push({
            uid: completion.uid,
            rowKind: 'list',
            codeName: list.codeName,
            unit: list.unit,
            price: list.price,
            designQuantity: list.designQuantity,
            doneQuantity: listOthers,
            residueQuantity: listBase,
            rateBase: listBase,
            completedQuantities: qty,
            oldCompleted: qty,
            doneRate: listRate,
            uploadDate: toUploadDateText(completion.uploadDate || upload.uploadDate),
            remark: completion.remark || '',
            uploadUid: upload.uid,
            listBreakdownCode: list.listBreakdownCode,
            listBreakdownUid: list.listBreakdownUid || list.uid,
            progressUid: item.progressUid || item.uid,
            subitemUid: item.subitemUid,
            orgUid: item.orgUid || list.orgUid,
            originValues: {
              completedQuantities: qty,
              doneRate: listRate,
              uploadDate: toUploadDateText(completion.uploadDate || upload.uploadDate),
              remark: completion.remark || ''
            },
            changedFields: {},
            children: null
          })
        })
      })
      const qty = Number(upload.completedQuantities || 0)
      const dateRate = Math.min(100, rateFromRemainQty(qty, rateBase))
      return {
        uid: upload.uid,
        rowKind: 'date',
        codeName: toUploadDateText(upload.uploadDate) || '未填写日期',
        unit: item.unit,
        designQuantity: item.designQuantity,
        doneQuantity: othersDone,
        residueQuantity: rateBase,
        rateBase: rateBase,
        completedQuantities: qty,
        oldCompleted: qty,
        doneRate: dateRate,
        uploadDate: toUploadDateText(upload.uploadDate),
        remark: upload.remark || '',
        progressUid: item.progressUid || item.uid,
        subitemUid: item.subitemUid,
        orgUid: item.orgUid,
        originValues: {
          completedQuantities: qty,
          doneRate: dateRate,
          uploadDate: toUploadDateText(upload.uploadDate),
          remark: upload.remark || ''
        },
        changedFields: {},
        children: listRows.length ? listRows : null
      }
    })
    if (!dateRows.length) return
    subMap[sid].children.push({
      uid: 'cat-' + (item.progressUid || item.uid),
      rowKind: 'category',
      isCategory: true,
      progressItemCode: item.progressItemCode,
      progressItemTypeUid: item.progressItemTypeUid,
      codeName: item.codeName,
      unit: item.unit,
      designQuantity: item.designQuantity,
      doneQuantity: othersDone,
      residueQuantity: rateBase,
      rateBase: rateBase,
      othersDoneQuantity: othersDone,
      progressUid: item.progressUid || item.uid,
      subitemUid: item.subitemUid,
      orgUid: item.orgUid,
      children: dateRows
    })
  })
  return sortProgressBizTree(
    rollupEditTree(
      grouped.filter((item) => item.children && item.children.length),
      { viewMode }
    )
  )
}
const EDIT_TRACK_FIELDS = [
  'completedQuantities',
  'doneRate',
  'uploadDate',
  'remark'
]
const sameEditValue = (left, right) => {
  const emptyLeft = left === undefined || left === null || left === ''
  const emptyRight = right === undefined || right === null || right === ''
  if (emptyLeft && emptyRight) return true
  if (emptyLeft || emptyRight) return false
  const numLeft = Number(left)
  const numRight = Number(right)
  if (!isNaN(numLeft) && !isNaN(numRight) && String(left).trim() !== '' && String(right).trim() !== '') {
    if (/^\d{4}-\d{2}-\d{2}/.test(String(left)) || /^\d{4}-\d{2}-\d{2}/.test(String(right))) {
      return String(left) === String(right)
    }
    return numLeft === numRight
  }
  return String(left) === String(right)
}
const refreshEditChangedFields = (row) => {
  if (!row || !row.originValues) {
    if (row) row.changedFields = {}
    return
  }
  const changed = {}
  EDIT_TRACK_FIELDS.forEach((key) => {
    if (!sameEditValue(row[key], row.originValues[key])) {
      changed[key] = true
    }
  })
  row.changedFields = changed
}
const markEditTreeChanged = (rows) => {
  const list = rows || []
  list.forEach((row) => {
    if (!row) return
    refreshEditChangedFields(row)
    if (row.children && row.children.length) markEditTreeChanged(row.children)
  })
  return rows
}
const isEditRowDirty = (row) => {
  return !!(row && row.changedFields && Object.keys(row.changedFields).length)
}
const flattenEditSaveRows = (nodes) => {
  const uploads = []
  const lists = []
  const walk = (rows) => {
    const source = rows || []
    source.forEach((node) => {
      if (!node) return
      if (node.rowKind === 'date') {
        if (isEditRowDirty(node)) uploads.push(node)
        const children = node.children || []
        children.forEach((child) => {
          if (child && child.rowKind === 'list' && isEditRowDirty(child)) {
            lists.push(child)
          }
        })
        return
      }
      if (node.children && node.children.length) walk(node.children)
    })
  }
  walk(nodes)
  return { uploads, lists }
}
const groupProgressBySubitem = (rows) => {
  const grouped = []
  const map = {}
  const list = rows || []
  list.forEach((item) => {
    const sid = item && item.subitemUid
    if (!sid) return
    if (!map[sid]) {
      map[sid] = {
        uid: 'sub-' + sid,
        subitemUid: sid,
        codeName: ((item.subitemCode || '') + '\xa0\xa0' + (item.subitemName || '')).trim(),
        subitemCode: item.subitemCode,
        subitemName: item.subitemName,
        isSubitemGroup: true,
        rowKind: 'subitem',
        children: []
      }
      grouped.push(map[sid])
    }
    map[sid].children.push(item)
  })
  const groupedRows = grouped.length ? grouped : rows || []
  groupedRows.forEach((node) => {
    if (node && node.children && node.children.length) {
      node.children = sortProgressBizTree(node.children)
    }
  })
  return sortProgressBizTree(groupedRows)
}
const rollupProgressTree = (nodes) => {
  (nodes || []).forEach((node) => {
    if (!node || !node.children || !node.children.length) return
    rollupProgressTree(node.children)
    PROGRESS_SUM_KEYS.forEach((key) => {
      node[key] = getSameAttribute(node.children, key).addValue
    })
  })
  return nodes
}
const setNull = (data, keys) => {
  if (!data.children || data.children.length === 0) {
    return data
  }

  const isTargetLevel = data.children.every(
    (child) => !child.children || child.children.length === 0
  )

  if (!isTargetLevel) {
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index]
      data[key] = null
    }
    for (const child of data.children) {
      setNull(child, keys)
    }
  } else {
    data.currentDoneRate = (
      decimalDiv(data.currentDoneQuantity, data.imageQuantity) * 100
    ).toFixed(2)
  }
  return data
}
const isSubitemStatRow = (row) => {
  if (!row) return false
  if (row.subitemCode) return true
  if (row.subitemUid && row.subitemName) return true
  return false
}
const applyProgressTypeGroupStats = (nodes) => {
  const list = nodes || []
  list.forEach((row) => {
    if (!row) return
    const kids = row.children || []
    if (kids.length) applyProgressTypeGroupStats(kids)
    const isTypeGroup =
      kids.length > 0 && kids.every((child) => !isSubitemStatRow(child))
    if (!isTypeGroup) return
    row.isProgressTypeGroup = true
    row.rowKind = row.rowKind || 'typeGroup'
    const currentRatio = decimalDiv(row.currentDoneAmount, row.listDesignAmount)
    if (currentRatio != null && !isNaN(Number(currentRatio))) {
      row.currentDoneRate = (Number(currentRatio) * 100).toFixed(2)
    }
  })
  return nodes
}
export default {
  namespaced: true,
  state: {
    progressItems: [], // 缓存形象进度数据
    subitemProgressInfo: [], // 当前分项进度详情
    progressUpload: [], // 当前进度分项进度填报详情
    listCompletion: [], // 当前进度分项对应清单填报详情
    editProgress: [], // 当前编辑的相关进度填报数据
    progressSnapshot: [],
    editQuery: null,
    viewFilter: {
      subitemUid: '',
      progressTypeUid: '',
      progressTypeCode: ''
    },
    reportSubitemUids: [],
    boundProgressTypeItems: []
  },
  getters: {
    progressTree: (state) => {
      const progressItems = JSON.parse(JSON.stringify(state.progressItems))
      var progressTree = getTreeArray(progressItems, {
        keepKeys: ['unit'],
        sumKeys: PROGRESS_SUM_KEYS
      })
      rollupProgressTree(progressTree)
      progressTree = sortProgressBizTree(progressTree)
      progressTree.forEach((item, index) => {
        progressTree[index] = setNull(item, [
          'unit',
          'imageQuantity',
          'currentDoneQuantity',
          'currentDoneRate'
        ])
      })
      applyProgressTypeGroupStats(progressTree)
      return progressTree
    },
    boundProgressTypeTree: (state) => {
      const items = JSON.parse(JSON.stringify(state.boundProgressTypeItems || []))
      return pruneEmptyBoundTypeTree(sortProgressBizTree(getTreeArray(items)))
    },
    boundProgressCategoryTree: (state, getters) => {
      return pruneToProgressTypeLevel(getters.boundProgressTypeTree)
    },
    progressCategoryTree: (state) => {
      const items = JSON.parse(JSON.stringify(state.progressItems || []))
      items.forEach((item) => {
        if (!item) return
        item.value = item.uid
        if (!item.label) {
          item.label = item.subitemCode
            ? (
                (item.subitemCode || '') +
                '\xa0\xa0' +
                (item.subitemName || '')
              ).trim()
            : (
                (item.progressItemCode || '') +
                '\xa0\xa0' +
                (item.progressItemType || '')
              ).trim()
        }
      })
      return pruneToProgressTypeLevel(
        sortProgressBizTree(getTreeArray(items))
      )
    }
  },
  actions: {
    // 获取形象进度数据
    async getProgressItems({ rootState, rootGetters, commit, dispatch }, data) {
      const period =
        data && data.period
          ? data.period
          : rootState.queryValues.imageProgress
          ? rootState.queryValues.imageProgress.period
          : [daysFormat(new Date()), daysFormat(new Date())]
      const storedType =
        data && data.periodType
          ? data.periodType
          : rootState.queryValues.imageProgress &&
            rootState.queryValues.imageProgress.periodType
          ? rootState.queryValues.imageProgress.periodType
          : ''
      const periodType =
        storedType ||
        inferPeriodType(
          period && period[0],
          period && period[1],
          getBidCreateDate({ state: rootState })
        )
      var queryValues = {
        orgUid:
          data && data.orgUid
            ? data.orgUid
            : rootState.queryValues.imageProgress &&
              rootState.queryValues.imageProgress.orgUid
            ? rootState.queryValues.imageProgress.orgUid
            : getDefaultBidOrgUid({
                orgType: rootGetters['user/orgType'],
                orgValue: rootState.user.orgValue,
                state: rootState
              }),
        subitemUid:
          data && data.subitemUid
            ? data.subitemUid
            : rootState.queryValues.imageProgress
            ? rootState.queryValues.imageProgress.subitemUid
            : '',
        subitemLevel:
          data && data.subitemLevel
            ? data.subitemLevel
            : rootState.queryValues.imageProgress
            ? rootState.queryValues.imageProgress.subitemLevel
            : '',
        periodType: periodType,
        startTime:
          periodType === 'total'
            ? ''
            : data && data.startTime
            ? data.startTime
            : period
            ? period[0]
            : daysFormat(new Date()),
        endTime:
          periodType === 'total'
            ? ''
            : data && data.endTime
            ? data.endTime
            : period
            ? period[1]
            : daysFormat(new Date())
      }
      queryValues = removeNull(queryValues)
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/getprogressitem',
          data: queryValues
        },
        { root: true }
      )
      //console.log(res)
      var progressItems = res.data ? res.data : []
      progressItems.forEach((item) => {
        item.value =
          data && data.subitem && item.subitemUid ? item.subitemUid : item.uid
        item.label = item.subitemCode
          ? item.subitemCode + '\xa0\xa0' + item.subitemName
          : item.progressItemCode + '\xa0\xa0' + item.progressItemType
        item.codeName = item.label
        item.currentDoneQuantity = item.currentDoneQuantity
          ? item.currentDoneQuantity
          : item.subitemUid
          ? 0
          : null
        item.currentDoneRate = (
          decimalDiv(item.currentDoneQuantity, item.imageQuantity) * 100
        ).toFixed(2)
        item.currentDoneAmount = item.currentDoneAmount
          ? item.currentDoneAmount
          : item.subitemUid
          ? 0
          : null
        item.contractAmount = item.contractAmount
          ? item.contractAmount
          : item.subitemUid
          ? 0
          : null
        item.listDesignAmount = listDesignByDoneQty(item)
      })
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'progressItems',
          value: progressItems
        },
        { root: true }
      )
    },
    async getBoundProgressTypeTree(
      { rootState, rootGetters, commit, dispatch },
      data
    ) {
      const orgUid =
        (data && data.orgUid) ||
        (rootState.queryValues.imageProgress &&
          rootState.queryValues.imageProgress.orgUid) ||
        getDefaultBidOrgUid({
          orgType: rootGetters['user/orgType'],
          orgValue: rootState.user.orgValue,
          state: rootState
        })
      if (!orgUid) {
        commit(
          'baseSet',
          { module: 'progress', key: 'boundProgressTypeItems', value: [] },
          { root: true }
        )
        return []
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/getboundprogressitems',
          data: { orgUid }
        },
        { root: true }
      )
      const items = (res && res.data ? res.data : [])
        .map(decorateBoundProgressTypeItem)
        .filter((item) => item && item.uid)
      commit(
        'baseSet',
        { module: 'progress', key: 'boundProgressTypeItems', value: items },
        { root: true }
      )
      return items
    },
    // 根据分项数据uid获取形象进度数据
    async getProgressBySubitemUid({ rootState, rootGetters, commit, dispatch }, uid) {
      const queryOrg =
        rootState.queryValues.imageProgress &&
        rootState.queryValues.imageProgress.orgUid
      const rowOrg =
        rootState.currentRow &&
        rootState.currentRow.imageProgress &&
        rootState.currentRow.imageProgress.orgUid
      const orgUid =
        queryOrg ||
        rowOrg ||
        getDefaultBidOrgUid({
          orgType: rootGetters['user/orgType'],
          orgValue: rootState.user.orgValue,
          state: rootState
        })
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/getprogressitembysubitem',
          data: {
            subitemUid: uid,
            orgUid: orgUid || ''
          }
        },
        { root: true }
      )
      //console.log(res)
      var subitemProgressInfo = (res.data ? res.data : []).filter(
        (item) => item && item.subitemUid
      )
      var progressUpload = []
      var listCompletion = []
      subitemProgressInfo.forEach((item) => {
        item.progressUid = item.uid
        item.rowKind = 'image'
        item.codeName =
          item.progressItemCode + '\xa0\xa0' + item.progressItemType
        item.designQuantity = item.imageQuantity
        item.children = item.listHook ? item.listHook : []
        item.children.forEach((child) => {
          child.listBreakdownUid = child.uid
          child.rowKind = 'list'
          child.codeName =
            child.listBreakdownCode + '\xa0\xa0' + child.listBreakdownName
          child.designQuantity = child.billCheckQuantity
            ? child.billCheckQuantity
            : 0
          child.marker = child.drawingNo ? child.drawingNo : ''
          child.listCompletion = child.listCompletion
            ? child.listCompletion
            : []
          child.doneQuantity = getSameAttribute(
            child.listCompletion,
            'completedQuantities'
          ).addValue
          applyRemainFields(child, child.doneQuantity)
          child.listCompletion = child.listCompletion.map((list) =>
            Object.assign({}, child, list)
          )
        })
        item.children = sortProgressBizTree(item.children)
        listCompletion = listCompletion.concat(item.children)
        item.progressUploadList = item.progressUploadList
          ? item.progressUploadList
          : []
        item.doneQuantity = getSameAttribute(
          item.progressUploadList,
          'completedQuantities'
        ).addValue
        applyRemainFields(item, item.doneQuantity)
        item.progressUploadList = item.progressUploadList.map((list) =>
          Object.assign({}, item, list)
        )
        progressUpload = progressUpload.concat(item.progressUploadList)
      })
      progressUpload = JSON.parse(JSON.stringify(progressUpload))
      progressUpload = progressUpload.filter((item) => {
        delete item.children
        item.doneRate = (
          decimalDiv(item.completedQuantities, item.imageQuantity) * 100
        ).toFixed(2)
        return (
          rootState.currentRow &&
          rootState.currentRow.imageProgress &&
          item.progressUid === rootState.currentRow.imageProgress.uid
        )
      })
      listCompletion = JSON.parse(JSON.stringify(listCompletion))
      listCompletion = listCompletion.filter((item) => {
        item.label = item.codeName
        item.children = item.listCompletion
        item.completedQuantities = item.doneQuantity
        item.doneRate = (
          decimalDiv(item.completedQuantities, item.designQuantity) * 100
        ).toFixed(2)
        item.children.forEach((child) => {
          child.label = child.uploadDate
          child.doneRate = (
            decimalDiv(child.completedQuantities, child.designQuantity) * 100
          ).toFixed(2)
          child.completedAmount = decimalMul(
            child.completedQuantities,
            child.price
          ).toFixed(2)
        })
        item.completedAmount = getSameAttribute(
          item.children,
          'completedAmount'
        ).addValue
        return (
          rootState.currentRow &&
          rootState.currentRow.imageProgress &&
          item.progressUid === rootState.currentRow.imageProgress.uid
        )
      })
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'progressUpload',
          value: progressUpload
        },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'listCompletion',
          value: listCompletion
        },
        { root: true }
      )
      const grouped = sortProgressBizTree(
        refreshCategoryRateCaps(
          resetNewReportInputs(
            pruneCompletedReportRows(groupProgressBySubitem(subitemProgressInfo))
          )
        )
      )
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'subitemProgressInfo',
          value: grouped
        },
        { root: true }
      )
      return res
    },
    // 挂接形象进度分项
    async addProgressItem({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/temp/progressitems',
          data: data,
          msg: '挂接形象进度分项成功'
        },
        { root: true }
      )
      return res
    },
    // 编辑形象进度分项信息
    async editProgressItem({ state, dispatch }, data) {
      var params = data ? data : state.progressItems
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/progressitem',
          data: params,
          msg: '编辑形象进度分项信息成功'
        },
        { root: true }
      )
      return res
    },
    // 删除形象进度分项
    async deleteProgressItem({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/temp/progressitem',
          data: { uid: uid },
          msg: '删除形象进度分项成功'
        },
        { root: true }
      )
      return res
    },
    async prepareReportBySubitems({ rootState, rootGetters, commit }, data) {
      const selected =
        (data && data.subitemUids) ||
        (rootState.formValues.selectReportSubitem &&
          rootState.formValues.selectReportSubitem.subitemUids) ||
        []
      const tree = rootGetters['division/progressSubitem'] || []
      const uids = collectLeafUidsByIds(tree, selected)
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'reportSubitemUids',
          value: uids
        },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'subitemProgressInfo',
          value: []
        },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'progressSnapshot',
          value: []
        },
        { root: true }
      )
      if (!uids.length) {
        return { code: 0 }
      }
      return { code: 200 }
    },
    async loadReportBySubitems({ state, dispatch }) {
      const uids = state.reportSubitemUids || []
      if (!uids.length) {
        return { code: 200, data: [] }
      }
      return dispatch('getProgressBySubitemUid', uids.join(','))
    },
    // 填报进度数据
    async reportProgress({ state, dispatch }, data) {
      var reportProgress = flattenReportRows(
        data ? data : state.subitemProgressInfo
      )
      var params = []
      reportProgress.forEach((item) => {
        if (item && item.isSubitemGroup) return
        item.listCompletion = !item.children
          ? null
          : item.children.filter(
              (child) =>
                child.completedQuantities && remainingOf(child) > 0
            )
        if (remainingOf(item) <= 0) {
          item.completedQuantities =
            item.listCompletion && item.listCompletion.length ? 0 : null
        }
        item.completedQuantities = item.completedQuantities
          ? item.completedQuantities
          : item.listCompletion && item.listCompletion.length
          ? 0
          : null
        item.uploadDate = item.completedQuantities
          ? item.uploadDate
          : item.listCompletion && item.listCompletion.length
          ? item.listCompletion[0].uploadDate
          : null
        if (
          item.completedQuantities ||
          (item.listCompletion && item.listCompletion.length)
        ) {
          params.push(item)
        }
      })
      const res = await dispatch(
        'baseRequest',
        {
          method: 'POST',
          url: '/organize/temp/progressupload',
          data: params,
          msg: '进度填报成功'
        },
        { root: true }
      )
      return res
    },
    // 批量编辑进度填报数据
    async editProgressUpload({ dispatch }, data) {
      if (!data || (Array.isArray(data) && !data.length)) {
        return { code: 200, status: 'SUCCESS' }
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/progressupload',
          data: data,
          msg: '编辑进度填报数据成功'
        },
        { root: true }
      )
      return res
    },
    // 删除进度填报数据
    async deleteProgressUpload({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/temp/progressupload',
          data: { uid: uid },
          msg: '删除进度填报数据成功'
        },
        { root: true }
      )
      return res
    },
    // 批量编辑清单完成数据
    async editListCompletion({ dispatch }, data) {
      if (!data || (Array.isArray(data) && !data.length)) {
        return { code: 200, status: 'SUCCESS', msg: '编辑成功' }
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/temp/listcompletion',
          data: data,
          msg: '编辑清单完成数据成功'
        },
        { root: true }
      )
      return res
    },
    // 删除清单完成数据
    async deleteListCompletion({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'DELETE',
          url: '/organize/temp/listcompletion',
          data: { uid: uid },
          msg: '删除清单完成数据成功'
        },
        { root: true }
      )
      return res
    },
    commitEditTree({ commit }, tree) {
      const rows = tree || []
      commit(
        'baseSet',
        { module: 'progress', key: 'editProgress', value: rows },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'progressSnapshot',
          value: JSON.parse(JSON.stringify(rows))
        },
        { root: true }
      )
      commit(
        'expandRowKeysSet',
        { key: 'editProgress', value: collectTreeUids(rows) },
        { root: true }
      )
      commit(
        'expandRowKeysSet',
        { key: 'viewProgress', value: collectTreeUids(rows) },
        { root: true }
      )
    },
    restoreProgressSnapshot({ state, commit }, key) {
      const snapshot = JSON.parse(JSON.stringify(state.progressSnapshot || []))
      if (key === 'reportProgress') {
        commit(
          'baseSet',
          { module: 'progress', key: 'subitemProgressInfo', value: snapshot },
          { root: true }
        )
        return
      }
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'editProgress',
          value: markEditTreeChanged(snapshot)
        },
        { root: true }
      )
    },
    snapshotReportProgress({ state, commit }) {
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'progressSnapshot',
          value: JSON.parse(JSON.stringify(state.subitemProgressInfo || []))
        },
        { root: true }
      )
      commit(
        'expandRowKeysSet',
        {
          key: 'reportProgress',
          value: collectTreeUids(state.subitemProgressInfo || [])
        },
        { root: true }
      )
    },
    async noopProgressQuery() {
      return { code: 200 }
    },
    primeEditQuery({ rootState, rootGetters, commit }, data) {
      const query = data || {}
      const imageQuery = rootState.queryValues.imageProgress || {}
      const viewMode =
        query.mine === '0' ||
        query.mine === 0 ||
        !!(
          query.viewFilter &&
          (query.viewFilter.subitemUid ||
            query.viewFilter.progressTypeUid ||
            query.viewFilter.progressTypeCode)
        )
      const periodType = viewMode
        ? query.periodType || imageQuery.periodType || ''
        : query.periodType || 'total'
      const period = viewMode
        ? query.period || imageQuery.period || []
        : query.period || []
      const payload = removeNull({
        orgUid:
          query.orgUid ||
          imageQuery.orgUid ||
          getDefaultBidOrgUid({
            orgType: rootGetters['user/orgType'],
            orgValue: rootState.user.orgValue,
            state: rootState
          }),
        subitemUid: Array.isArray(query.subitemUid)
          ? query.subitemUid.filter(Boolean).join(',')
          : query.subitemUid || '',
        periodType: periodType,
        startTime: periodType === 'total' ? '' : period[0] || '',
        endTime: periodType === 'total' ? '' : period[1] || '',
        mine:
          query.mine != null
            ? query.mine
            : rootState.user.userType === 'per'
            ? '1'
            : ''
      })
      commit(
        'baseSet',
        { module: 'progress', key: 'editQuery', value: payload },
        { root: true }
      )
      commit(
        'baseSet',
        {
          module: 'progress',
          key: 'viewFilter',
          value: {
            subitemUid:
              (query.viewFilter && query.viewFilter.subitemUid) || '',
            progressTypeUid:
              (query.viewFilter && query.viewFilter.progressTypeUid) || '',
            progressTypeCode:
              (query.viewFilter && query.viewFilter.progressTypeCode) || ''
          }
        },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'progress', key: 'editProgress', value: [] },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'progress', key: 'progressSnapshot', value: [] },
        { root: true }
      )
      return payload
    },
    async loadProgressUploads({ rootState, rootGetters, commit, dispatch }, data) {
      const query = data || {}
      const imageQuery = rootState.queryValues.imageProgress || {}
      const viewMode =
        query.viewMode === true ||
        (rootState.currentStep &&
          rootState.currentStep.step === 'viewProgressList')
      const periodType = viewMode
        ? query.periodType || imageQuery.periodType || ''
        : query.periodType || 'total'
      const period = viewMode
        ? query.period || imageQuery.period || []
        : query.period || []
      const orgUid =
        query.orgUid ||
        imageQuery.orgUid ||
        getDefaultBidOrgUid({
          orgType: rootGetters['user/orgType'],
          orgValue: rootState.user.orgValue,
          state: rootState
        })
      const mine =
        query.mine != null
          ? query.mine
          : rootState.user.userType === 'per'
          ? '1'
          : ''
      const payload = removeNull({
        orgUid: orgUid,
        subitemUid: Array.isArray(query.subitemUid)
          ? query.subitemUid.filter(Boolean).join(',')
          : query.subitemUid || '',
        periodType: periodType,
        startTime: periodType === 'total' ? '' : period[0] || '',
        endTime: periodType === 'total' ? '' : period[1] || '',
        mine: mine
      })
      commit(
        'baseSet',
        { module: 'progress', key: 'editQuery', value: payload },
        { root: true }
      )
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/temp/getprogressuploads',
          data: payload
        },
        { root: true }
      )
      let rows = res && res.data ? res.data : []
      if (viewMode) {
        const allow = {}
        ;(rootState.progress.progressItems || []).forEach((item) => {
          if (item && item.subitemUid && item.uid) {
            allow[String(item.uid)] = true
          }
        })
        if (Object.keys(allow).length) {
          rows = rows.filter((item) => item && item.uid && allow[String(item.uid)])
        }
      }
      if (!viewMode && rootState.user.userType === 'per') {
        rows = keepLastMineUploads(rows)
      }
      const tree = buildEditTree(decorateProgressItems(rows), {
        othersAsZero: !viewMode && rootState.user.userType !== 'per',
        viewMode
      })
      await dispatch('commitEditTree', tree)
      return { code: 200, data: tree }
    },
    async reloadEditProgress({ state, dispatch }) {
      if (!state.editQuery) return { code: 200 }
      return dispatch('loadProgressUploads', Object.assign({}, state.editQuery))
    },
    async saveEditProgress({ state, dispatch }) {
      const packed = flattenEditSaveRows(state.editProgress)
      if (!packed.uploads.length && !packed.lists.length) {
        return { code: 0, msg: '没有修改需要提交' }
      }
      if (packed.uploads.length) {
        const res = await dispatch('editProgressUpload', packed.uploads)
        if (res.code !== 200) return res
      }
      if (packed.lists.length) {
        const res = await dispatch('editListCompletion', packed.lists)
        if (res.code !== 200) return res
      }
      return { code: 200, status: 'SUCCESS' }
    },
    syncEditTree({ commit }, rows) {
      const tree = refreshCategoryRateCaps(
        rollupEditTree(markEditTreeChanged(rows || []))
      )
      commit(
        'baseSet',
        { module: 'progress', key: 'editProgress', value: tree },
        { root: true }
      )
    }
  }
}
